import React, { useRef, useState } from 'react';
import productImageService, {
  ProductImage,
} from '../../services/api/productImageService';

interface Props {
  role: 'logo' | 'product';
  images: ProductImage[];
  selectedId: number | null;
  productId: number;
  onSelect: (imageId: number | null) => void;
  onUploaded: (image: ProductImage) => void;
  /** Promise を返すと、解決するまでボタンを無効化して再押下を無視する。 */
  onNext: () => void | Promise<unknown>;
  onBack: () => void;
  /** 次へ押下後の保存・生成開始中。ボタンを無効化し「準備中...」を出す（二重押下防止）。 */
  nextLoading?: boolean;
  /** 処理中にボタンの下へ出す説明。待ち時間がある処理で「押せていない」と誤解させないため。 */
  nextLoadingMessage?: string;
}

const CONTAINER = 'text-center';
const TITLE = 'm-0 mb-2 text-gray-800 text-[1.45rem]';
const DESCRIPTION =
  'mx-auto mt-0 mb-6 text-gray-500 text-[0.9rem] leading-[1.7]';
const GRID =
  'grid grid-cols-[repeat(auto-fill,minmax(132px,1fr))] gap-3 max-h-[430px] overflow-auto p-1';
// Choice 共通（padding / display は派生側で指定するため base には含めない）
const CHOICE_BASE =
  'relative min-h-[132px] rounded-xl overflow-hidden cursor-pointer border-2 ' +
  'transition-[transform,border-color] duration-[180ms] ease-[ease] ' +
  'hover:-translate-y-0.5 hover:border-[#8b93d8] ' +
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[rgba(97,111,201,0.35)] focus-visible:outline-offset-2 ' +
  '[&_img]:w-full [&_img]:h-32 [&_img]:object-contain [&_img]:bg-slate-50 [&_img]:block';
const CHOICE_SELECTED =
  'border-[#616fc9] bg-[#f4f5ff] shadow-[0_0_0_3px_rgba(97,111,201,0.14)]';
const CHOICE_UNSELECTED = 'border-gray-200 bg-white shadow-none';
const CHOICE = `${CHOICE_BASE} p-0`;
const NONE_CHOICE = `${CHOICE_BASE} flex flex-col items-center justify-center gap-2 text-gray-500 text-[0.85rem] p-[18px]`;
const UPLOAD_CHOICE = `${NONE_CHOICE} disabled:cursor-default disabled:transform-none disabled:border-gray-200`;
const SPINNER =
  'w-[22px] h-[22px] border-[3px] border-[#dfe2ea] border-t-primary rounded-full animate-[spin_0.8s_linear_infinite]';
const SPINNER_SMALL =
  'w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-[spin_0.8s_linear_infinite]';
const UPLOAD_ERROR = 'mx-auto mt-2.5 mb-0 text-red-700 text-[0.82rem]';
const NEXT_STATUS = 'mx-auto mt-3 mb-0 text-gray-500 text-[0.82rem]';
const CHECK =
  'absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center bg-primary text-white font-extrabold';
const ACTIONS = 'flex justify-center gap-3 mt-6';
const BUTTON_BASE =
  'min-h-[44px] py-0 px-[22px] rounded-[9px] border font-bold cursor-pointer hover:brightness-[0.96]';
const BUTTON_PRIMARY = 'border-primary bg-primary text-white';
const BUTTON_DEFAULT = 'border-gray-300 bg-white text-gray-600';

const SignupImageRoleStep: React.FC<Props> = ({
  role,
  images,
  selectedId,
  productId,
  onSelect,
  onUploaded,
  onNext,
  onBack,
  nextLoading = false,
  nextLoadingMessage,
}) => {
  const isLogo = role === 'logo';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // 親の nextLoading は再描画まで反映されないため、同じフレーム内の連打は ref で止める。
  const nextPendingRef = useRef(false);
  const [nextPending, setNextPending] = useState(false);
  const busy = nextLoading || nextPending;

  const handleNext = () => {
    if (busy || nextPendingRef.current) return;
    const result = onNext();
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
    nextPendingRef.current = true;
    setNextPending(true);
    // 失敗時の表示は親が担う。ここでは押せる状態に戻すだけ。
    (result as Promise<unknown>)
      .catch(() => undefined)
      .finally(() => {
        nextPendingRef.current = false;
        setNextPending(false);
      });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // 同じファイルを選び直しても onChange が発火するよう value をリセットする。
    event.target.value = '';
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const image = await productImageService.uploadImage(
        productId,
        file,
        isLogo ? 'brand_logo' : 'product_image',
      );
      onUploaded(image);
    } catch {
      setUploadError(
        'アップロードに失敗しました。10MB以下の画像を選択してください。',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={CONTAINER}>
      <h2 className={TITLE}>
        {isLogo
          ? 'ロゴを1枚選んでください'
          : '商品・サービス画像を1枚選んでください'}
      </h2>
      <p className={DESCRIPTION}>
        {isLogo
          ? 'バナー内のブランド表現に使います。ロゴがない場合は「選択しない」を選べます。'
          : '広告の主役として優先的に使います。適切な画像がない場合は選択しなくても進めます。'}
      </p>
      <div className={GRID}>
        <button
          className={`${NONE_CHOICE} ${
            selectedId === null ? CHOICE_SELECTED : CHOICE_UNSELECTED
          }`}
          type="button"
          onClick={() => onSelect(null)}
        >
          {selectedId === null && (
            <span className={CHECK} aria-hidden>
              ✓
            </span>
          )}
          <span style={{ fontSize: '1.6rem' }}>∅</span>
          <strong>選択しない</strong>
        </button>
        <button
          className={`${UPLOAD_CHOICE} ${CHOICE_UNSELECTED}`}
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          aria-label="画像をアップロード"
        >
          {uploading ? (
            <>
              <span className={SPINNER} aria-hidden />
              <strong>アップロード中...</strong>
            </>
          ) : (
            <>
              <span style={{ fontSize: '1.6rem' }} aria-hidden>
                ⬆
              </span>
              <strong>画像をアップロード</strong>
            </>
          )}
        </button>
        {images.map((image) => (
          <button
            className={`${CHOICE} ${
              selectedId === image.id ? CHOICE_SELECTED : CHOICE_UNSELECTED
            }`}
            type="button"
            key={image.id}
            onClick={() => onSelect(image.id)}
            aria-label={`${image.title || image.file_name}を選択`}
          >
            {selectedId === image.id && (
              <span className={CHECK} aria-hidden>
                ✓
              </span>
            )}
            <img
              src={image.url || image.signed_url}
              alt={image.title || image.file_name}
            />
          </button>
        ))}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {uploadError && (
        <p className={UPLOAD_ERROR} role="alert">
          {uploadError}
        </p>
      )}
      <div className={ACTIONS}>
        <button
          className={`${BUTTON_BASE} ${BUTTON_DEFAULT}`}
          type="button"
          onClick={onBack}
          disabled={busy}
        >
          戻る
        </button>
        <button
          className={`${BUTTON_BASE} ${BUTTON_PRIMARY} inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait`}
          type="button"
          onClick={handleNext}
          disabled={busy}
          aria-busy={busy}
        >
          {busy && <span className={SPINNER_SMALL} aria-hidden />}
          {busy ? '準備中...' : 'この内容で次へ'}
        </button>
      </div>
      {busy && nextLoadingMessage && (
        <p className={NEXT_STATUS} role="status">
          {nextLoadingMessage}
        </p>
      )}
    </div>
  );
};

export default SignupImageRoleStep;
